import * as UploadConstants from './RSUSoftwareUploadConstants';

/**
 * RSU Software Upload Reducer
 * Manages state for software upload operations including form data, validation, and upload progress
 */

/**
 * Initial state for RSU Software Upload management
 */
const initialState = {
    // Form data fields
    productLine: '',
    itemRefNumber: '',
    instrumentModel: '',
    instrumentMod: '',
    softwareName: '',
    softwareVersion: '',
    softwareBriefDescription: '',
    packageFile: null,

    // Upload status and progress
    isUploading: false,
    uploadError: null,
    uploadSuccess: false,
    isUploadSuccess: false,
    isUploadError: false,
    uploadStatus: { 
        percent: 0, 
        speed: 0, 
        estimatedTime: '' 
    },

    // Datalake information
    isFetchingDatalakeInfo: false,
    datalakeInfo: null,
    datalakeInfoError: null,

    // Field validation states
    isProductLineErr: false,
    productLineMsgErr: '',
    isItemRefNumberErr: false,
    itemRefNumberMsgErr: '',
    isInstrumentModelErr: false,
    instrumentModelMsgErr: '',
    isInstrumentModErr: false,
    instrumentModMsgErr: '',
    isSoftwareNameErr: false,
    softwareNameMsgErr: '',
    isSoftwareVersionErr: false,
    softwareVersionMsgErr: '',
    isSoftwareBriefDescriptionErr: false,
    softwareBriefDescriptionMsgErr: '',
    isPackageFileErr: false,
    packageFileMsgErr: '',
};

/**
 * Utility function to capitalize the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} - The capitalized string
 */
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * RSU Software Upload Reducer
 * Handles all state management for software upload operations
 * @param {Object} state - Current state
 * @param {Object} action - Redux action
 * @returns {Object} - New state
 */
const RSUSoftwareUploadReducer = (state = initialState, action) => {
    switch (action.type) {
        case UploadConstants.SET_UPLOAD_FORM_FIELD:
            return {
                ...state,
                [action.payload.fieldName]: action.payload.value,
                [`is${capitalize(action.payload.fieldName)}Err`]: false,
                [`${action.payload.fieldName}MsgErr`]: '',
                uploadError: null,
                uploadSuccess: false,
            };

        case UploadConstants.SET_UPLOAD_FIELD_ERROR:
            return {
                ...state,
                [`is${capitalize(action.payload.fieldName)}Err`]: action.payload.isError,
                [`${action.payload.fieldName}MsgErr`]: action.payload.message,
            };

        case UploadConstants.CLEAR_UPLOAD_FORM:
            return {
                ...initialState,
            };

        case UploadConstants.UPLOAD_SOFTWARE_REQUEST:
            return {
                ...state,
                uploadError: null,
                uploadSuccess: false,
                isUploadError: false,
                isUploadSuccess: false,
            };

        case UploadConstants.UPLOAD_STARTED:
            return {
                ...state,
                uploadError: null,
                uploadSuccess: false,
                isUploading: true,
                isUploadError: false,
                isUploadSuccess: false,
                uploadStatus: { 
                    percent: 0, 
                    speed: 0, 
                    estimatedTime: '' 
                },
            };

        case UploadConstants.TRACK_UPLOAD_PROGRESS:
            return {
                ...state,
                uploadStatus: {
                    percent: action.payload.percent || 0,
                    speed: action.payload.speed || 0,
                    estimatedTime: action.payload.estimatedTime || ''
                },
            };

        case UploadConstants.UPLOAD_SOFTWARE_SUCCESS:
            return {
                ...state,
                isUploading: false,
                isUploadSuccess: true,
                isUploadError: false,
                uploadError: null,
                uploadSuccess: action.payload,
                datalakeInfo: null,
                datalakeInfoError: null,
                uploadStatus: { 
                    percent: 100, 
                    speed: 0, 
                    estimatedTime: 'Complete' 
                },
            };

        case UploadConstants.UPLOAD_SOFTWARE_FAILURE:
            return {
                ...state,
                isUploading: false,
                isUploadError: true,
                isUploadSuccess: false,
                uploadError: action.payload.error || 'Upload failed. Please try again.',
                uploadSuccess: false,
                uploadStatus: { 
                    percent: 0, 
                    speed: 0, 
                    estimatedTime: '' 
                },
            };

        case UploadConstants.CLEAR_UPLOAD_ALERTS:
            return {
                ...state,
                uploadError: null,
                uploadSuccess: null,
                isUploadError: false,
                isUploadSuccess: false,
            };

        case UploadConstants.FETCH_DATALAKE_DETAILS_REQUEST:
            return {
                ...state,
                isFetchingDatalakeInfo: true,
                datalakeInfoError: null,
            };

        case UploadConstants.FETCH_DATALAKE_DETAILS_SUCCESS:
            return {
                ...state,
                isFetchingDatalakeInfo: false,
                datalakeInfo: action.payload,
                datalakeInfoError: null,
            };

        case UploadConstants.FETCH_DATALAKE_DETAILS_FAILURE:
            return {
                ...state,
                isFetchingDatalakeInfo: false,
                datalakeInfo: null,
                datalakeInfoError: action.payload?.error || 'Failed to fetch DataLake information. Please try again.',
            };

        default:
            return state;
    }
};

export default RSUSoftwareUploadReducer;
